import { InvoiceFormTypes } from "models/formTypes";
import { WintFormConnect } from "models/propTypes/wintFormPropsTypes";
import { UtilsHelper } from "utils/helpers";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { NestedKeyOf } from "models/utilTypes/nestedKeyType";
import useGetMailOptionDefaults from "./useGetMailOptionDefaults";

function useSetMailOptionsDefaults(connect: WintFormConnect<InvoiceFormTypes.Create>, values: InvoiceFormTypes.Create) {
  const { getCreateInvoiceDefaultEmailOptions, getEmailToDefault } = useGetMailOptionDefaults();

  const shouldSetDefaultEmailTo = (
    toEmailFieldName: NestedKeyOf<Required<InvoiceFormTypes.Create>>,
    sendMailToSettings?: InvoiceFormTypes.DefaultEmailToSettings
  ) => {
    const sendToMailProps = connect(toEmailFieldName);

    return getEmailIsDefault(
      sendToMailProps.value,
      getEmailToDefault(
        values.mailOptions?.defaultEmailTo.referencePerson,
        values.mailOptions?.defaultEmailTo.customer,
        sendMailToSettings
      )
    );
  };

  const getEmailIsDefault = (currentEmails?: AutocompleteListItem[], currentdefaultsShouldBe?: string) => {
    const emailsList = currentEmails?.map((x: AutocompleteListItem) => x.value) ?? [];
    const defaultEmails = UtilsHelper.splitMultipleEmails(currentdefaultsShouldBe ?? "");
    return UtilsHelper.areStringArraysEqual(defaultEmails, emailsList);
  };

  const updateAutoReminderEmailOptions = (newReferencePerson: AutocompleteListItem | null) => {
    const reminderEmailToName = "otherSettings.automaticReminderSendMail.emailTo";
    const autoReminderSendToMailProps = connect(reminderEmailToName);

    if (shouldSetDefaultEmailTo(reminderEmailToName, values.otherSettings.defaultReminderEmailToSettings)) {
      const newDefaultEmailTo = getEmailToDefault(
        newReferencePerson?.data?.mail,
        values.mailOptions?.defaultEmailTo.customer,
        values.otherSettings.defaultReminderEmailToSettings
      );

      autoReminderSendToMailProps.setValue(
        UtilsHelper.splitMultipleEmails(newDefaultEmailTo).map(
          (x: string): AutocompleteListItem => ({
            id: x,
            value: x,
          })
        )
      );
    }
  };

  const updateRecurringInvoiceEmailOptions = (newReferencePerson: AutocompleteListItem | null) => {
    const recurringEmailToName = "recurringSettings.recurringSendMail.emailTo";
    const recurringSendToMailProps = connect(recurringEmailToName);

    if (shouldSetDefaultEmailTo(recurringEmailToName, values.defaultEmailToSettings)) {
      const newDefaultEmailTo = getEmailToDefault(
        newReferencePerson?.data?.mail,
        values.mailOptions?.defaultEmailTo.customer,
        values.defaultEmailToSettings
      );
      recurringSendToMailProps.setValue(
        UtilsHelper.splitMultipleEmails(newDefaultEmailTo).map(
          (x: string): AutocompleteListItem => ({
            id: x,
            value: x,
          })
        )
      );
    }
  };

  const updatePreviewEmailOptions = (newReferencePerson: AutocompleteListItem | null) => {
    const previewEmailToName = "previewSendMail.emailTo";
    const previewSendToMailProps = connect(previewEmailToName);

    if (shouldSetDefaultEmailTo(previewEmailToName, values.defaultEmailToSettings)) {
      const newDefaultEmailTo = getEmailToDefault(
        newReferencePerson?.data?.mail,
        values.mailOptions?.defaultEmailTo.customer,
        values.defaultEmailToSettings
      );

      previewSendToMailProps.setValue(
        UtilsHelper.splitMultipleEmails(newDefaultEmailTo).map(
          (x: string): AutocompleteListItem => ({
            id: x,
            value: x,
          })
        )
      );
    }
  };

  const updateDefaultEmailToOptions = (referencePerson: AutocompleteListItem | null) => {
    updateAutoReminderEmailOptions(referencePerson);
    updateRecurringInvoiceEmailOptions(referencePerson);
    updatePreviewEmailOptions(referencePerson);

    const referencePersonEmail = referencePerson?.data?.mail;
    const defaultEmailToProps = connect("mailOptions.defaultEmailTo.referencePerson");
    defaultEmailToProps.setValue(referencePersonEmail);

    if (referencePersonEmail) {
      const mailToListProps = connect("mailOptions.mailToList");
      if (!mailToListProps.value?.some((i: AutocompleteListItem) => i.value === referencePersonEmail)) {
        mailToListProps.setValue([
          ...(mailToListProps.value ?? []),
          { id: referencePersonEmail, value: referencePersonEmail },
        ]);
      }
    }
  };

  const getUpdatedDefaultEmailOptions = (
    sendMailValues: InvoiceFormTypes.SendMail,
    sendMailOptions: InvoiceFormTypes.SendMailOptions,
    sendMailToSettings?: InvoiceFormTypes.DefaultEmailToSettings,
    formValuesPrev?: {
      referencePersonEmail?: string;
      customerEmail?: string;
      sendMailToSettings?: InvoiceFormTypes.DefaultEmailToSettings;
    }
  ) => {
    const defaultValues = getCreateInvoiceDefaultEmailOptions(sendMailOptions, sendMailToSettings, {});

    if (sendMailValues.emailFromType === undefined) {
      sendMailValues.emailFromType = defaultValues.emailFromType;
      sendMailValues.emailFrom = defaultValues.emailFrom;
    }

    if (sendMailValues.emailMessage === undefined) {
      sendMailValues.emailMessage = defaultValues.emailMessage;
    }

    if (sendMailValues.emailSubject === undefined) {
      sendMailValues.emailSubject = defaultValues.emailSubject;
    }

    if (
      formValuesPrev === undefined ||
      getEmailIsDefault(
        sendMailValues.emailTo ?? [],
        getEmailToDefault(
          formValuesPrev.referencePersonEmail,
          formValuesPrev.customerEmail,
          formValuesPrev.sendMailToSettings
        )
      )
    ) {
      sendMailValues.emailTo = defaultValues.emailTo;
    }

    return sendMailValues;
  };

  return { updateDefaultEmailToOptions, getUpdatedDefaultEmailOptions };
}

export default useSetMailOptionsDefaults;
